import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AppLayout } from "@/components/intel/Layout";
import { ModuleShell } from "@/components/intel/ModuleShell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SourceLabPanel } from "@/components/nexus/SourceLabPanel";
import { listNexusInvestigations, loadNexusInvestigation, startNexusInvestigation } from "@/lib/nexus/nexus.functions";
import { analyzeInvestigationWithAi, getAiProviderStatus } from "@/lib/ai/ai.functions";
import { MultiModelPanel } from "@/components/nexus/MultiModelPanel";
import { NexusReformDisclaimer, NexusResultDisclaimer } from "@/components/nexus/NexusReformNotice";
import type { NexusEntity, NexusReport } from "@/lib/nexus/types";
import { AlertTriangle, ExternalLink, GitBranch, Loader2, Search, ShieldQuestion, Target } from "lucide-react";

export const Route = createFileRoute("/nexus")({
  head: () => ({
    meta: [
      { title: "Nexus Orchestrator · Adaptive OSINT Investigations" },
      {
        name: "description",
        content:
          "Plan, route and run multi-source OSINT investigations with adaptive pivoting, evidence provenance, corroboration scoring, contradiction detection and gap analysis.",
      },
      { property: "og:title", content: "Nexus Orchestrator · Adaptive OSINT Investigations" },
      {
        property: "og:description",
        content: "Adaptive multi-source intelligence orchestration with full provenance and discovery paths.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: NexusPage,
});

const ENTITY_TYPES = ["person", "company", "email", "phone", "username", "domain", "ip", "location"] as const;

function confidenceTone(c: number): string {
  if (c >= 0.8) return "text-emerald-400 border-emerald-400/40";
  if (c >= 0.55) return "text-cyan border-cyan/40";
  return "text-amber-400 border-amber-400/40";
}

function EntityCard({ entity }: { entity: NexusEntity }) {
  const [open, setOpen] = useState(false);
  const isLink = /^https?:\/\//.test(entity.value);
  return (
    <li className="rounded-md border border-panel-border bg-background/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="font-mono text-[10px] uppercase">
          {entity.type}
        </Badge>
        {isLink ? (
          <a href={entity.value} target="_blank" rel="noreferrer noopener" className="break-all text-sm text-cyan hover:underline">
            {entity.label ?? entity.value}
            <ExternalLink className="ml-1 inline h-3 w-3" />
          </a>
        ) : (
          <span className="break-all text-sm">{entity.label ?? entity.value}</span>
        )}
        <Badge variant="outline" className={`font-mono text-[10px] ${confidenceTone(entity.confidence)}`}>
          {Math.round(entity.confidence * 100)}%
        </Badge>
        <Badge variant="outline" className="font-mono text-[10px]">
          depth {entity.depth}
        </Badge>
        <Badge variant="outline" className="font-mono text-[10px]">
          {entity.evidence.length} source{entity.evidence.length === 1 ? "" : "s"}
        </Badge>
        <button type="button" onClick={() => setOpen((v) => !v)} className="ml-auto text-[11px] text-muted-foreground hover:text-foreground">
          {open ? "Hide evidence" : "Show evidence"}
        </button>
      </div>
      {open && (
        <div className="mt-2 space-y-2 border-t border-panel-border pt-2">
          {Object.entries(entity.attributes).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(entity.attributes).slice(0, 10).map(([k, v]) => (
                <span key={k} className="rounded bg-panel px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                  {k}: {String(v).slice(0, 60)}
                </span>
              ))}
            </div>
          )}
          <ul className="space-y-1.5">
            {entity.evidence.map((ev, i) => (
              <li key={i} className="text-[11px] text-muted-foreground">
                <span className="text-foreground">{ev.sourceLabel}</span> · {ev.tier} · {ev.parser} ·{" "}
                {new Date(ev.retrievedAt).toLocaleString()}
                {ev.url && (
                  <>
                    {" "}
                    <a href={ev.url} target="_blank" rel="noreferrer noopener" className="text-cyan hover:underline">
                      source
                    </a>
                  </>
                )}
                {ev.snippet && <div className="mt-0.5 italic">“{ev.snippet}”</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

function ReportView({ report, investigationId }: { report: NexusReport; investigationId?: string }) {
  const analyze = useServerFn(analyzeInvestigationWithAi);
  const providerStatus = useServerFn(getAiProviderStatus);
  const [ai, setAi] = useState(report.ai ?? null);
  const providers = useQuery({ queryKey: ["ai-providers"], queryFn: () => providerStatus(), staleTime: 300_000 });
  const rerun = useMutation({
    mutationFn: () => analyze({ data: { id: investigationId! } }),
    onSuccess: (b) => setAi(b),
  });

  const byType = new Map<string, NexusEntity[]>();
  for (const e of report.entities) {
    if (e.depth === 0) continue;
    byType.set(e.type, [...(byType.get(e.type) ?? []), e]);
  }
  const groups = [...byType.entries()].sort((a, b) => b[1].length - a[1].length);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[
          ["Entities", report.entities.length],
          ["Sources run", report.stats.jobsRun],
          ["Requests", report.stats.requests],
          ["Runtime", `${Math.round(report.stats.runtimeMs / 1000)}s`],
        ].map(([label, value]) => (
          <div key={String(label)} className="rounded-md border border-panel-border bg-panel/60 p-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="text-lg">{value}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="entities">
        <TabsList className="flex-wrap">
          <TabsTrigger value="entities">Findings</TabsTrigger>
          <TabsTrigger value="graph">Relationships</TabsTrigger>
          <TabsTrigger value="gaps">Gaps &amp; pivots</TabsTrigger>
          <TabsTrigger value="conflicts">Contradictions</TabsTrigger>
          <TabsTrigger value="jobs">Source runs</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="ai">AI reasoning</TabsTrigger>
        </TabsList>

        <TabsContent value="entities" className="space-y-4 pt-3">
          {groups.length === 0 && <p className="text-sm text-muted-foreground">No entities passed the quality firewall for this target.</p>}
          {groups.map(([type, list]) => (
            <div key={type}>
              <div className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {type} · {list.length}
              </div>
              <ul className="space-y-1.5">
                {list.slice(0, 40).map((e) => (
                  <EntityCard key={e.id} entity={e} />
                ))}
              </ul>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="graph" className="pt-3">
          <ul className="space-y-1.5">
            {report.relationships.slice(0, 200).map((r, i) => {
              const from = report.entities.find((e) => e.id === r.from);
              const to = report.entities.find((e) => e.id === r.to);
              return (
                <li key={i} className="rounded-md border border-panel-border bg-background/40 p-2 text-sm">
                  <GitBranch className="mr-1.5 inline h-3.5 w-3.5 text-cyan" />
                  <span>{from?.label ?? from?.value ?? r.from}</span>
                  <Badge variant="outline" className="mx-2 font-mono text-[10px]">
                    {r.kind}
                  </Badge>
                  <span>{to?.label ?? to?.value ?? r.to}</span>
                  <span className="ml-2 font-mono text-[10px] text-muted-foreground">{Math.round(r.confidence * 100)}%</span>
                </li>
              );
            })}
            {report.relationships.length === 0 && <p className="text-sm text-muted-foreground">No relationships were established.</p>}
          </ul>
        </TabsContent>

        <TabsContent value="gaps" className="space-y-3 pt-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-md border border-panel-border bg-panel/60 p-3">
              <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Established</div>
              <ul className="space-y-0.5 text-sm">{report.gaps.known.map((k) => <li key={k}>{k}</li>)}</ul>
            </div>
            <div className="rounded-md border border-panel-border bg-panel/60 p-3">
              <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Still unknown</div>
              <ul className="space-y-0.5 text-sm">{report.gaps.unknown.map((k) => <li key={k}>{k}</li>)}</ul>
              {report.gaps.unknown.length === 0 && <p className="text-sm text-muted-foreground">Nothing outstanding.</p>}
            </div>
          </div>
          <div className="rounded-md border border-panel-border bg-panel/60 p-3">
            <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Recommended next pivots</div>
            <ul className="space-y-1 text-sm">
              {report.gaps.nextPivots.map((p, i) => (
                <li key={i}>
                  <Target className="mr-1.5 inline h-3.5 w-3.5 text-cyan" />
                  {p.label} <span className="text-muted-foreground">— {p.rationale}</span>
                </li>
              ))}
              {report.gaps.nextPivots.length === 0 && <p className="text-sm text-muted-foreground">No further pivots recommended.</p>}
            </ul>
          </div>
          {report.gaps.weaklySupported.length > 0 && (
            <div className="rounded-md border border-amber-400/30 bg-amber-400/5 p-3">
              <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-amber-400">Single-source, treat with caution</div>
              <ul className="space-y-0.5 text-sm">{report.gaps.weaklySupported.slice(0, 20).map((w) => <li key={w}>{w}</li>)}</ul>
            </div>
          )}
        </TabsContent>

        <TabsContent value="conflicts" className="pt-3">
          {report.contradictions.length === 0 && <p className="text-sm text-muted-foreground">No conflicting claims were detected.</p>}
          <ul className="space-y-2">
            {report.contradictions.map((c, i) => (
              <li key={i} className="rounded-md border border-amber-400/30 bg-amber-400/5 p-3">
                <div className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <span className="font-mono text-[11px] uppercase">{c.field}</span>
                  <span>{c.subject}</span>
                </div>
                <ul className="mt-1.5 space-y-1 text-[12px]">
                  {c.claims.map((claim, j) => (
                    <li key={j} className="text-muted-foreground">
                      <span className="text-foreground">{claim.value}</span> — {claim.sourceLabel}
                      {claim.publishedAt ? ` (${claim.publishedAt})` : ""}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-[12px] text-muted-foreground">{c.note}</p>
              </li>
            ))}
          </ul>
        </TabsContent>

        <TabsContent value="jobs" className="pt-3">
          <ul className="space-y-1">
            {report.jobs.map((j, i) => (
              <li key={i} className="flex flex-wrap items-center gap-2 rounded-md border border-panel-border bg-background/40 p-2 text-[12px]">
                <Badge variant="outline" className="font-mono text-[10px]">
                  {j.status}
                </Badge>
                <span className="text-foreground">{j.label}</span>
                <span className="text-muted-foreground">on {j.target}</span>
                <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                  depth {j.depth} · {j.discoveries} found · {j.requests} req · {Math.round(j.latencyMs / 100) / 10}s
                </span>
                {j.error && <span className="w-full text-[11px] text-amber-400">{j.error}</span>}
              </li>
            ))}
          </ul>
        </TabsContent>

        <TabsContent value="rejected" className="pt-3">
          <p className="mb-2 text-[12px] text-muted-foreground">
            Values the entity firewall refused, so they never reach the graph or a report.
          </p>
          <ul className="space-y-1">
            {report.rejected.map((r, i) => (
              <li key={i} className="rounded-md border border-panel-border bg-background/40 p-2 text-[12px]">
                <ShieldQuestion className="mr-1.5 inline h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-mono">{r.type}</span> · {r.value} — <span className="text-muted-foreground">{r.reason}</span>
              </li>
            ))}
            {report.rejected.length === 0 && <p className="text-sm text-muted-foreground">Nothing was rejected.</p>}
          </ul>
        </TabsContent>

        <TabsContent value="ai" className="pt-3">
          <MultiModelPanel
            ai={ai}
            providers={providers.data ?? []}
            {...(investigationId ? { onRerun: () => rerun.mutate(), running: rerun.isPending } : {})}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function NexusPage() {
  const start = useServerFn(startNexusInvestigation);
  const list = useServerFn(listNexusInvestigations);
  const load = useServerFn(loadNexusInvestigation);

  const [target, setTarget] = useState("");
  const [entityType, setEntityType] = useState<(typeof ENTITY_TYPES)[number]>("person");
  const [mode, setMode] = useState<"quick" | "standard" | "deep">("standard");
  const [country, setCountry] = useState("");
  const [company, setCompany] = useState("");
  const [report, setReport] = useState<NexusReport | null>(null);
  const [reportId, setReportId] = useState<string | undefined>(undefined);

  const history = useQuery({ queryKey: ["nexus-history"], queryFn: () => list() });

  const run = useMutation({
    mutationFn: () =>
      start({
        data: {
          target: target.trim(),
          entityType,
          mode,
          ...(country.trim().length === 2 ? { country: country.trim().toUpperCase() } : {}),
          ...(company.trim() ? { company: company.trim() } : {}),
        },
      }),
    onSuccess: (res) => {
      setReport(res.report);
      setReportId(res.id || undefined);
      void history.refetch();
    },
  });

  const open = useMutation({
    mutationFn: (id: string) => load({ data: { id } }).then((r) => ({ r, id })),
    onSuccess: ({ r, id }) => {
      setReport(r);
      setReportId(id);
    },
  });

  return (
    <AppLayout>
      <ModuleShell
        eyebrow="Orchestration"
        title="Nexus Orchestrator"
        description="Adaptive multi-source investigation: capability routing, bounded pivoting, corroborated evidence and gap analysis."
      >
        <div className="space-y-6">
          <NexusReformDisclaimer />
          <Tabs defaultValue="investigate">
            <TabsList>
              <TabsTrigger value="investigate">Investigate</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
              <TabsTrigger value="lab">Source Lab</TabsTrigger>
            </TabsList>

            <TabsContent value="investigate" className="space-y-4 pt-4">
              <div className="grid gap-2 md:grid-cols-[2fr_1fr_1fr_auto]">
                <Input
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  placeholder="Name, company, email, phone, username, domain or IP"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && target.trim().length > 1) run.mutate();
                  }}
                />
                <Select value={entityType} onValueChange={(v) => setEntityType(v as typeof entityType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quick">Quick</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="deep">Deep</SelectItem>
                  </SelectContent>
                </Select>
                <Button disabled={run.isPending || target.trim().length < 2} onClick={() => run.mutate()}>
                  {run.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Search className="mr-1.5 h-4 w-4" />}
                  Investigate
                </Button>
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                <Input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country hint (ISO code, e.g. FI)" maxLength={2} />
                <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Known organisation (optional, sharpens routing)" />
              </div>
              <NexusResultDisclaimer />
              {run.isPending && (
                <p className="text-sm text-muted-foreground">
                  Routing sources and following the strongest leads. A standard run takes up to about 90 seconds.
                </p>
              )}
              {run.isError && <p className="text-sm text-destructive">The investigation could not be completed. Please try again.</p>}
              {report && <ReportView key={reportId ?? report.generatedAt} report={report} {...(reportId ? { investigationId: reportId } : {})} />}
            </TabsContent>

            <TabsContent value="history" className="pt-4">
              <ul className="space-y-1.5">
                {(history.data ?? []).map((h) => (
                  <li key={h.id}>
                    <button
                      type="button"
                      onClick={() => open.mutate(h.id)}
                      className="flex w-full flex-wrap items-center gap-2 rounded-md border border-panel-border bg-background/40 p-2.5 text-left text-sm hover:border-cyan/50"
                    >
                      <Badge variant="outline" className="font-mono text-[10px] uppercase">
                        {h.entityType}
                      </Badge>
                      <span>{h.target}</span>
                      <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                        {h.mode} · {h.requests} req · {new Date(h.createdAt).toLocaleString()}
                      </span>
                    </button>
                  </li>
                ))}
                {(history.data ?? []).length === 0 && <p className="text-sm text-muted-foreground">No investigations yet.</p>}
              </ul>
            </TabsContent>

            <TabsContent value="lab" className="pt-4">
              <SourceLabPanel />
            </TabsContent>
          </Tabs>
        </div>
      </ModuleShell>
    </AppLayout>
  );
}
